import {
  configuredModelsEndpoint,
  MODELS_HELPER_TEXT,
  MODELS_PREFIX,
  openAiModelsFetch,
  withAllowedModelsHint,
} from "../models";
import { openaiUsage } from "../openai/usage";
import { bearerAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const liteLlm = defineConnector({
  identifier: "liteLlm",
  name: "LiteLLM",
  tagLine: "Open-source LLM gateway",
  logo,
  protocol: "http",
  tags: ["ai", "engineering"],
  defaultUrlPattern: ({ baseUrl }) => domainToRegex(baseUrl),
  auth: (app) => bearerAuth(app.secret.apiKey),
  steps: [
    {
      title: "Create a virtual key",
      items: [
        {
          kind: "markdown",
          content:
            "In your LiteLLM proxy, open **Virtual Keys** and create a key, or call [`/key/generate`](https://docs.litellm.ai/docs/proxy/virtual_keys) with your master key. LiteLLM authenticates requests with `Authorization: Bearer <LITELLM_API_KEY>`.",
        },
      ],
    },
    {
      title: "Enter your LiteLLM details",
      items: [
        {
          kind: "url",
          label: "Base URL",
          placeholder: "https://litellm.example.com/v1",
          required: true,
          target: { kind: "configuration", field: "baseUrl" },
          helperText:
            "Your proxy’s OpenAI-compatible endpoint including the /v1 path, e.g. https://litellm.example.com/v1.",
        },
        {
          kind: "input",
          label: "Virtual key",
          placeholder: "sk-…",
          required: true,
          target: { kind: "secrets", field: "apiKey" },
        },
      ],
    },
    {
      title: "Choose available models (optional)",
      items: [
        {
          kind: "list",
          label: "Available models",
          prefix: MODELS_PREFIX,
          columns: [{ field: "name", placeholder: "gpt-5" }],
          addLabel: "Add model",
          fetch: openAiModelsFetch(
            "Fetch models from LiteLLM",
            configuredModelsEndpoint
          ),
          helperText: MODELS_HELPER_TEXT,
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: configuredModelsEndpoint,
  },
  publicEnvVars: ({ baseUrl }) => ({
    LITELLM_BASE_URL: baseUrl || "",
  }),
  usageHint: (configuration) =>
    withAllowedModelsHint(
      "Use process.env.LITELLM_BASE_URL as the OpenAI-compatible base URL. Pass a placeholder API key; the proxy injects the real virtual key from the connector. Call it with the OpenAI API shape; LiteLLM answers in that shape for every provider it fronts, so there is no need to switch dialects per model.",
      configuration
    ),
  usage: openaiUsage(),
});
